import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRepairContext } from "@/context/RepairContext";
import { Loader2, TrendingUp, DollarSign, Calculator } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  subMonths,
} from "date-fns";
import { RepairItem } from "@/types";

// Helper function to calculate the final price of a repair including modifications
const calculateFinalPrice = (repair: RepairItem): number => {
  // Start with the base repair price
  let finalPrice = repair.repairPrice || 0;

  // Add all price modifications if they exist
  if (repair.priceModifications && repair.priceModifications.length > 0) {
    const modificationsTotal = repair.priceModifications.reduce(
      (sum, mod) => sum + (mod.amount || 0),
      0
    );
    finalPrice += modificationsTotal;
  }

  return finalPrice;
};

// Helper function to calculate the final cost of a repair including modifications
const calculateFinalCost = (repair: RepairItem): number => {
  // Start with the base repair cost
  let finalCost = repair.repairCost || 0;

  // Add all cost modifications if they exist
  if (repair.costModifications && repair.costModifications.length > 0) {
    const modificationsTotal = repair.costModifications.reduce(
      (sum, mod) => sum + (mod.amount || 0),
      0
    );
    finalCost += modificationsTotal;
  }

  return finalCost;
};

// Helper function to calculate earnings (revenue - cost)
const calculateEarnings = (repair: RepairItem): number => {
  return calculateFinalPrice(repair) - calculateFinalCost(repair);
};

interface EarningsData {
  date: string;
  earnings: number;
  revenue: number;
  cost: number;
  formattedDate: string;
}

const EarningsWidget: React.FC = () => {
  const { t } = useTranslation();
  const { repairs } = useRepairContext();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<EarningsData[]>([]);
  const [stats, setStats] = useState({
    totalEarnings: 0,
    totalRevenue: 0,
    totalCost: 0,
    monthlyEarnings: 0,
    monthlyRevenue: 0,
    monthlyCost: 0,
    previousMonthEarnings: 0,
    percentChange: 0,
    profitMargin: 0,
  });

  // Format currency helper
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("ar-TN", {
      style: "currency",
      currency: "TND",
      maximumFractionDigits: 3,
    }).format(amount);
  };

  useEffect(() => {
    if (repairs) {
      // Get paid repairs - consider ALL repairs that are marked as paid
      const paidRepairs = repairs.filter((r) => r.paymentStatus === "paid");

      // Calculate total earnings, revenue, and cost
      const totalRevenue = paidRepairs.reduce((sum, repair) => {
        return sum + calculateFinalPrice(repair);
      }, 0);

      const totalCost = paidRepairs.reduce((sum, repair) => {
        return sum + calculateFinalCost(repair);
      }, 0);

      const totalEarnings = totalRevenue - totalCost;

      // Calculate current month earnings
      const now = new Date();
      const currentMonthStart = startOfMonth(now);
      const currentMonthEnd = endOfMonth(now);

      const currentMonthRepairs = paidRepairs.filter(
        (r) =>
          r.completedAt &&
          new Date(r.completedAt) >= currentMonthStart &&
          new Date(r.completedAt) <= currentMonthEnd
      );

      const monthlyRevenue = currentMonthRepairs.reduce((sum, repair) => {
        return sum + calculateFinalPrice(repair);
      }, 0);

      const monthlyCost = currentMonthRepairs.reduce((sum, repair) => {
        return sum + calculateFinalCost(repair);
      }, 0);

      const monthlyEarnings = monthlyRevenue - monthlyCost;

      // Calculate previous month earnings
      const previousMonthStart = startOfMonth(subMonths(now, 1));
      const previousMonthEnd = endOfMonth(subMonths(now, 1));

      const previousMonthRepairs = paidRepairs.filter(
        (r) =>
          r.completedAt &&
          new Date(r.completedAt) >= previousMonthStart &&
          new Date(r.completedAt) <= previousMonthEnd
      );

      const previousMonthEarnings = previousMonthRepairs.reduce(
        (sum, repair) => {
          return sum + calculateEarnings(repair);
        },
        0
      );

      // Calculate percent change
      const percentChange =
        previousMonthEarnings === 0
          ? 100
          : ((monthlyEarnings - previousMonthEarnings) /
              previousMonthEarnings) *
            100;

      // Calculate profit margin
      const profitMargin =
        totalRevenue === 0 ? 0 : (totalEarnings / totalRevenue) * 100;

      setStats({
        totalEarnings,
        totalRevenue,
        totalCost,
        monthlyEarnings,
        monthlyRevenue,
        monthlyCost,
        previousMonthEarnings,
        percentChange,
        profitMargin,
      });

      // Prepare chart data for current month
      const daysInMonth = eachDayOfInterval({
        start: currentMonthStart,
        end: currentMonthEnd,
      });

      const dailyData = daysInMonth.map((day) => {
        const dayRepairs = paidRepairs.filter(
          (r) => r.completedAt && isSameDay(new Date(r.completedAt), day)
        );

        const revenue = dayRepairs.reduce((sum, repair) => {
          return sum + calculateFinalPrice(repair);
        }, 0);

        const cost = dayRepairs.reduce((sum, repair) => {
          return sum + calculateFinalCost(repair);
        }, 0);

        const earnings = revenue - cost;

        return {
          date: format(day, "yyyy-MM-dd"),
          formattedDate: format(day, "dd"),
          earnings,
          revenue,
          cost,
        };
      });

      setData(dailyData);
      setLoading(false);
    }
  }, [repairs]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4" dir="ltr">
        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
          <div className="flex items-center">
            <Calculator className="h-4 w-4 mr-1 text-green-500" />
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t("dashboard.monthlyEarnings")}
            </div>
          </div>
          <div className="text-2xl font-bold">
            {formatCurrency(stats.monthlyEarnings)}
          </div>
          <div className="flex items-center text-xs mt-1">
            {stats.percentChange > 0 ? (
              <TrendingUp className="h-3 w-3 mr-1 text-green-500" />
            ) : (
              <TrendingUp className="h-3 w-3 mr-1 text-red-500 transform rotate-180" />
            )}
            <span
              className={
                stats.percentChange > 0 ? "text-green-500" : "text-red-500"
              }
            >
              {Math.abs(stats.percentChange).toFixed(1)}%
            </span>
          </div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
          <div className="flex items-center">
            <DollarSign className="h-4 w-4 mr-1 text-blue-500" />
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t("dashboard.profitMargin")}
            </div>
          </div>
          <div className="text-2xl font-bold">
            {stats.profitMargin.toFixed(1)}%
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {formatCurrency(stats.totalEarnings)} /{" "}
            {formatCurrency(stats.totalRevenue)}
          </div>
        </div>

        <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
          <div className="flex items-center">
            <Calculator className="h-4 w-4 mr-1 text-amber-500" />
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t("dashboard.totalCost")}
            </div>
          </div>
          <div className="text-2xl font-bold">
            {formatCurrency(stats.totalCost)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t("dashboard.monthlyCost")}: {formatCurrency(stats.monthlyCost)}
          </div>
        </div>
      </div>

      <div className="h-[120px] mt-4" dir="ltr">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{
              top: 5,
              right: 5,
              left: 0,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="formattedDate" tick={{ fontSize: 10 }} />
            <YAxis
              tickFormatter={(value) => `${value} TND`}
              tick={{ fontSize: 10 }}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                formatCurrency(value),
                name === "earnings"
                  ? t("dashboard.earnings")
                  : name === "revenue"
                  ? t("dashboard.revenue")
                  : t("dashboard.cost"),
              ]}
              contentStyle={{
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                borderRadius: "4px",
                border: "1px solid #e2e8f0",
                padding: "8px",
              }}
            />
            <Bar dataKey="earnings" fill="#10b981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default EarningsWidget;
